// Per spec/blocks.md §images (P-037.28 / D-136). Multi-image grid Block —
// a fork of `event-card-group` with everything ABOVE the image dropped
// (no 10 px navy bar, no title, no date+location, no CTA). The only
// per-item input is the optional `imageUrl` (fallback to
// `/fallback-image.png` per D-131 §11).
//
// **Layout dispatch on `inputs.columns`** (P-037.30 sixth post-Phase-Close §D
// moved `columns` from `options` → `inputs` so staff can pick the layout
// per-Email rather than locking it at the Template scope; schema v12 → v13
// migration relocates persisted values per-record-kind: Template instances'
// `options.columns` → `defaultInputs.columns` (preserves the staff-intended
// default for Emails created from the Template post-migration); Email
// instances' `options.columns` → `inputs.columns` (preserves rendered look
// of existing Emails)):
// - **`'3'`**: existing 190 × 100 cells in the system 3-col grid
//   (30/190/25/190/25/190/30 per D-117), 20 px inter-row gap.
// - **`'1'`**: single image per row, 620 × 326 each (= 190 × 3 + 25 × 2
//   for the width; aspect 1.9:1 preserved at the larger size →
//   620 / 1.9 ≈ 326 height), 20 px inter-row gap when multiple.
//
// **Image-cell edge: borderless** (P-037.30 §item B — Banks dropped the
// 1 px IMG border previously applied via D-133 IMAGE_BORDER, completing
// the migration off IMAGE_BORDER started for the card-style consumers at
// P-037.29 D-137 §item 4). The pre-baked inset shadow on the upload
// bytes (via `bakeInsetShadow: true` on the registry entry, post-Phase-
// Close §item G) still carries the cell-edge visual signal.
//
// **One @1x image-dimensions registry entry: 620 × 326.** The larger
// columns=1 case bounds the upload — `columns=3` just renders smaller.
// Avoids per-Block-instance dimension dispatch (registry is set at
// upload time, before staff picks columns). Engineering call: this
// is a quality-vs-bytes trade where favoring quality is the right
// call.
//
// **Auto-hide on empty `images` array.** Orchestrator-level skip per
// the shared `*.length === 0` convention.
//
// **P-037.30 ninth post-Phase-Close §B — per-item optional `linkUrl`.**
// When set, the rendered image wraps in an `<a href="${linkUrl}"
// target="_blank" rel="noopener noreferrer">` so the image is
// clickable. Absent → image renders unwrapped. Affects both
// columns paths uniformly via the shared `imageCell` helper. Additive
// optional field — no schema migration needed; old persisted state
// without `linkUrl` validates cleanly under Zod's `.optional()`.

import { z } from 'zod'
import { defineBlock } from '../render/define-block'
import { html, toAbsoluteUrl } from '../render'
import { image } from '../primitives/image'
import { gridLayout, FALLBACK_IMAGE_SRC, paddedRow } from '../components/helpers'
import { HtmlString } from '../types'

const imageItemSchema = z.object({
  imageUrl: z.string().url().describe('imageUrl').optional(),
  // P-037.30 ninth post-Phase-Close §B — per-item optional link. When
  // set, the rendered image wraps in an `<a href="${linkUrl}"
  // target="_blank" rel="noopener noreferrer">` (matches the link-
  // primitive convention from D-030 — secure-default `noopener
  // noreferrer` so the linked page can't navigate back through
  // `window.opener`). Absent → image renders unwrapped (status quo).
  // Explicit `.describe('Link URL')` overrides the form-generator
  // humanize-default ("Link Url") which keeps a trailing "Url"
  // (the strip-`Url` rule in `schema-to-fields.ts:206` only fires for
  // the `imageUrl` brand).
  linkUrl: z.string().url().describe('Link URL').optional(),
})

type ImageItem = z.infer<typeof imageItemSchema>

const optionsSchema = z.object({})

const inputsSchema = z.object({
  images: z.array(imageItemSchema),
  // P-037.30 sixth post-Phase-Close §D — moved from options → inputs so
  // staff can pick the layout per-Email. `.default('3')` lets pre-v13
  // persisted state that lacks the field validate cleanly (the migration
  // also relocates persisted values, but the default is the belt-and-
  // suspenders safety net for any Email that somehow lacks both).
  columns: z.enum(['1', '3']).default('3'),
})

// columns=3 dimensions (matches event-card-group's 190 × 100 image-cell).
const THREE_COL_IMG_WIDTH = 190
const THREE_COL_IMG_HEIGHT = 100
// columns=1 dimensions: 190×3 + 25×2 = 620 px width; aspect 1.9:1
// preserved → 620 / 1.9 ≈ 326 height. Rounds the aspect-preserved height
// to the nearest integer so the IMG attribute is whole-pixel.
const ONE_COL_IMG_WIDTH = 620
const ONE_COL_IMG_HEIGHT = 326
const ROW_GAP = 20

function imageCell(item: ImageItem, width: number, height: number): HtmlString {
  const src = item.imageUrl ?? FALLBACK_IMAGE_SRC
  // Decorative `alt=""` — the Block carries no semantic title for the
  // image; staff doesn't surface alt-text in this Block per Banks's
  // planning. If a future audit adds an `altText` field, dispatch on
  // its presence.
  const imgHtml = image({
    src,
    alt: '',
    width,
    height,
    align: 'center',
  })
  // P-037.30 ninth post-Phase-Close §B — per-item optional link wrap.
  // When `linkUrl` is set, wrap the rendered <img> in an <a> with the
  // link-primitive's D-030 secure-default `rel="noopener noreferrer"`
  // + `target="_blank"`. `toAbsoluteUrl` mirrors the link/button
  // primitives' D-134 absolute-href defense (relative paths emit
  // absolute in the rendered HTML so CC + View-in-Tab can resolve;
  // absolute URLs + CC variables pass through unchanged).
  if (item.linkUrl === undefined) return imgHtml
  const href = toAbsoluteUrl(item.linkUrl)
  return html`<a href="${href}" target="_blank" rel="noopener noreferrer">${imgHtml}</a>`
}

export const images = defineBlock({
  id: 'images',
  label: 'Images',
  category: 'content',
  optionsSchema,
  inputsSchema,
  defaultOptions: {},
  defaultInputs: { images: [], columns: '3' },
  autoHide: ({ inputs }) => inputs.images.length === 0,
  render: ({ inputs }) => {
    if (inputs.columns === '3') {
      const grid = gridLayout(inputs.images, 3, (item) =>
        imageCell(item, THREE_COL_IMG_WIDTH, THREE_COL_IMG_HEIGHT),
      )
      // 30 px L/R padded outer cell — matches the event-card-group /
      // chapter-sponsors outer rhythm. Top/bottom padding lives on
      // the system-level inter-Block spacer per P-037.29 D-137.
      // prettier-ignore
      return html`<table role="presentation" cellpadding="0" cellspacing="0" border="0" width="100%"><tr><td style="padding: 0 30px;">${grid}</td></tr></table>`
    }
    // inputs.columns === '1': stacked single-image rows with 20 px inter-row gap.
    const rows = inputs.images.map((item, i) => {
      const cell = imageCell(item, ONE_COL_IMG_WIDTH, ONE_COL_IMG_HEIGHT)
      // prettier-ignore
      return i === 0
        ? html`<tr><td>${cell}</td></tr>`
        : paddedRow(cell, `padding-top: ${ROW_GAP}px;`)
    })
    // prettier-ignore
    return html`<table role="presentation" cellpadding="0" cellspacing="0" border="0" width="100%"><tr><td style="padding: 0 30px;"><table role="presentation" cellpadding="0" cellspacing="0" border="0" width="100%">${rows}</table></td></tr></table>`
  },
})
